import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { PageHeader } from '../components/ui/PageHeader';
import { apiService } from '../services/apiService';
import { useToast } from '../contexts/ToastContext';
import { SearchRequest, PaginatedResponse } from '../types';

export const MyReportsPage: React.FC = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchData, setSearchData] = useState<PaginatedResponse<SearchRequest> | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [status] = useState('completed'); // Only show completed searches
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('Loading reports with params:', {
        page: currentPage,
        page_size: pageSize,
        status: status || undefined,
        search: search || undefined,
        ordering: '-created_at'
      });
      
      const response = await apiService.getSearchHistory({
        page: currentPage,
        page_size: pageSize,
        status: status || undefined,
        search: search || undefined,
        ordering: '-created_at'
      });
      
      console.log('Reports response received:', response);
      setSearchData(response);
      setError(null); // Clear any previous errors
    } catch (err: any) {
      console.error('Failed to load reports:', err);
      const errorMessage = err?.message || 'Failed to load reports';
      setError(errorMessage);
      showToast(errorMessage, 'error');
      // Set empty data on error so UI doesn't break
      setSearchData({
        count: 0,
        next: undefined,
        previous: undefined,
        results: []
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, status, search, showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPhoneNumber = (countryCode?: string | null, value?: string): string => {
    if (!countryCode || !value) return value || '';
    const clean = countryCode.startsWith('+') ? countryCode.slice(1) : countryCode;
    return `+${clean} ${value}`;
  };

  const activeResults = useMemo(() => {
    // API already filters by status, so we just use the results directly
    return searchData?.results || [];
  }, [searchData]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil((searchData?.count || 0) / pageSize));
  }, [searchData?.count, pageSize]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="My Reports"
        subtitle="View your completed search reports"
        align="center"
      />

      <Card>
        <div className="p-4">
          {/* Search Filter */}
          <div className="mb-4">
            <input
              type="text"
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search by query value..."
              className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-sm w-full md:w-64 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            />
          </div>

          {isLoading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <p className="mt-2 text-gray-500 dark:text-gray-400">Loading reports...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-red-600 dark:text-red-400 mb-4">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Error Loading Reports</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
              <Button onClick={() => loadData()} variant="outline">
                Try Again
              </Button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto custom-scrollbar border border-gray-200 dark:border-gray-700 rounded-xl">
                <table className="min-w-full text-sm text-gray-700 dark:text-gray-200">
                  <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-750 text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    <tr>
                      <th className="px-3.5 py-3 text-left">#</th>
                      <th className="px-3.5 py-3 text-left">Report</th>
                      <th className="px-3.5 py-3 text-left">Parameter Type</th>
                      <th className="px-3.5 py-3 text-left">Query</th>
                      <th className="px-3.5 py-3 text-left">Cost</th>
                      <th className="px-3.5 py-3 text-left">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeResults.length > 0 ? (
                      activeResults.map((row, idx) => (
                        <tr
                          key={row.id}
                          className={idx % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800'}
                        >
                          <td className="px-3 py-2">{(currentPage - 1) * pageSize + idx + 1}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/app/search/${row.id}/report`)}
                                title="View Report"
                              >
                                <svg
                                  className="w-5 h-5 text-indigo-600 dark:text-indigo-400"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                  />
                                </svg>
                              </Button>
                            </div>
                          </td>
                          <td className="px-3 py-2">{row.parameter_type_name || 'N/A'}</td>
                          <td className="px-3 py-2">
                            {row.parameter_type_name === 'Phone Number' && row.country_code
                              ? formatPhoneNumber(row.country_code, row.search_value)
                              : row.search_value}
                          </td>
                          <td className="px-3 py-2">{row.total_cost || 0} Coins</td>
                          <td className="px-3 py-2">{formatDateTime(row.created_at)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-gray-400 dark:text-gray-500">
                          No completed reports found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {searchData && searchData.count > 0 && totalPages > 1 && (
                <div className="flex justify-center mt-4 gap-2 flex-wrap">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mr-2">
                    Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, searchData.count)} of {searchData.count} reports
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    First
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  {(() => {
                    const pagesToShow: number[] = [];
                    const startPage = Math.max(1, currentPage - 1);
                    const endPage = Math.min(totalPages, currentPage + 1);
                    
                    // Always show first page
                    if (startPage > 1) {
                      pagesToShow.push(1);
                      if (startPage > 2) {
                        pagesToShow.push(-1); // Ellipsis marker
                      }
                    }
                    
                    // Show pages around current page
                    for (let i = startPage; i <= endPage; i++) {
                      pagesToShow.push(i);
                    }
                    
                    // Always show last page
                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pagesToShow.push(-1); // Ellipsis marker
                      }
                      pagesToShow.push(totalPages);
                    }
                    
                    return pagesToShow.map((page, idx) => {
                      if (page === -1) {
                        return (
                          <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">
                            ...
                          </span>
                        );
                      }
                      return (
                        <Button
                          key={page}
                          size="sm"
                          variant={currentPage === page ? 'default' : 'outline'}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </Button>
                      );
                    });
                  })()}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    Last
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

